/**
 * one-stop/blocks-appointments — the `appointments` block: the prototype's
 * "Rather not wait? Book a time".
 *
 * The row, lifted: service name and detail line on the left, the next opening in
 * brand text plus the CTA word on the right, the WHOLE ROW clickable, hovering to
 * the brand border and the soft card fill — a grid of rows on desktop, one
 * bordered list at 390 px.
 *
 * THE CTA: the row opens the flow for THAT slot — `holdOpening(serviceId,
 * opening)` then `checkoutHold(holdId, visitor)` — and the row itself carries the
 * held and confirmed states, so a student never leaves the page to find out
 * whether it worked. The opening is the fragment's own soonest one; its civil
 * time + zone are resolved to instants IN THE OPENING'S ZONE at the moment of
 * use, so the viewer's clock cannot move what was booked.
 *
 * THE HELD PANEL IS ALREADY A FULL-WIDTH SURFACE. It sets `display: block` on the
 * row and lays its own column out inside, so the details form spans the card —
 * checked against the events row's defect and it does not share it.
 *
 * THE HOLD DEADLINE IS A CLOCK, NOT A LABEL. `expiresAt` is a real five-minute
 * deadline, so the row COUNTS IT DOWN and withdraws Confirm at zero. A Confirm
 * left live past expiry can only 409 into `HOLD_EXPIRED`, and that is the one
 * outcome whose copy has to be careful — a Confirm that looked like it failed
 * may in fact have succeeded.
 *
 * AND THE SAME RULE AFTER A REFUSAL. `HOLD_INVALID` renders `book.maybeBooked`,
 * which points a visitor at their inbox precisely so they do not submit again;
 * leaving Confirm enabled underneath it handed them the one control the copy was
 * talking them out of, and `checkout` has no idempotency key (JAI-1160), so the
 * second tap is a second booking. On both spent states the row keeps the copy,
 * takes the control away, and offers the only move it has left: pick another
 * time.
 *
 * A 409 ON THE HOLD IS A NORMAL OUTCOME, NOT AN ERROR BANNER. The fragment is up
 * to 15 seconds old, so the opening the row rendered may already be gone.
 * `SLOT_UNAVAILABLE` / `HOLD_EXPIRED` therefore re-run availability for that
 * service and that day and show what is ACTUALLY free — which is the same path
 * as "pick a different time". Those searched slots go back into `createHold`
 * verbatim: same instants, same ids, no reformatting, no re-zoning.
 *
 * AND THE HOLD IS RELEASED ON THE WAY OUT. A hold that is neither converted nor
 * released keeps the slot off the board for everyone until it lapses, so Cancel
 * releases it rather than just closing the panel.
 *
 * Every state stays ONE element in the list, so the mobile rule that turns the
 * grid into a bordered list (and strips the last row's divider) keeps working.
 */
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import type { PortalAppointmentItem } from "@v0y4ge/api-contracts";
import { Button } from "@v0y4ge/jrni-ui";
import { useCta, useOneStopActions } from "./actions";
import type { AppointmentHold, AvailabilitySlot, BookingConfirmation } from "./actions";
import {
  configBool,
  configText,
  formatInstantTime,
  formatMinutes,
  formatMoney,
  formatWeekdayTime,
} from "./config";
import {
  Actions,
  CtaFeedback,
  Empty,
  ManagedReference,
  Section,
  SuccessPanel,
  VisitorFields,
  fragmentOf,
  useVisitor,
} from "./primitives";
import { plural, uiStrings } from "./strings";
import type { Translate } from "./strings";
import type { BlockRenderProps } from "./types";

function fieldSet(config: Record<string, unknown>): ReadonlySet<string> {
  const raw = config["fields"];
  const values = Array.isArray(raw) ? raw.filter((v): v is string => typeof v === "string") : null;
  return new Set(values ?? ["duration", "price", "staff", "location"]);
}

/* ── the hold clock ─────────────────────────────────────────────────── */

/** Whole seconds to a deadline, floored at zero; `null` when there is none. */
function secondsLeft(target: number): number | null {
  if (Number.isNaN(target)) return null;
  return Math.max(0, Math.ceil((target - Date.now()) / 1000));
}

/**
 * `m:ss`. Digits only and no unit word: the one WORD around it is a catalog key
 * (`book.holdLeft`), so the readout says "4:38 left" in English and "quedan
 * 4:38" in Spanish without a literal reaching the JSX.
 */
function clockOf(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

/**
 * The deadline, ticking. `null` when there is no deadline to count.
 *
 * The interval clears ITSELF at zero — a lapsed hold has nothing left to tick —
 * and re-arms when the deadline changes, so a second hold on the same row gets a
 * fresh clock rather than the previous one's. The instant is parsed once, here,
 * and never inside the tick.
 */
function useCountdown(deadline: string | null): number | null {
  const target = deadline === null ? Number.NaN : Date.parse(deadline);
  const [left, setLeft] = useState<number | null>(() => secondsLeft(target));
  useEffect(() => {
    setLeft(secondsLeft(target));
    if (Number.isNaN(target)) return undefined;
    const id = window.setInterval(() => {
      const next = secondsLeft(target);
      setLeft(next);
      if (next !== null && next <= 0) window.clearInterval(id);
    }, 1000);
    return () => window.clearInterval(id);
  }, [target]);
  return left;
}

export function AppointmentsBlock({ block, ctx }: BlockRenderProps): JSX.Element {
  const t = uiStrings(ctx.locale);
  const fragment = fragmentOf(block, "appointments");
  const title = configText(block.config, "title", ctx.locale) ?? t("appointments.title");
  const subtitle = configText(block.config, "subtitle", ctx.locale) ?? t("appointments.subtitle");
  const ctaLabel = configText(block.config, "ctaLabel", ctx.locale) ?? t("appointments.cta");
  const showNextOpenings = configBool(block.config, "showNextOpenings", true);
  const fields = fieldSet(block.config);

  if (!fragment || fragment.items.length === 0) {
    return (
      <Section title={title} subtitle={subtitle}>
        <Empty>{t("appointments.empty")}</Empty>
      </Section>
    );
  }

  const more = Math.max(0, fragment.total - fragment.items.length);

  return (
    <Section title={title} subtitle={subtitle}>
      <div className="lv-rowlist">
        {fragment.items.map((item) => (
          <ServiceRow
            key={item.serviceId}
            item={item}
            t={t}
            locale={ctx.locale}
            timeZone={ctx.timeZone}
            ctaLabel={ctaLabel}
            showNextOpenings={showNextOpenings}
            fields={fields}
          />
        ))}
      </div>
      {fragment.hasMore && more > 0 ? (
        <p className="lv-overflow">{plural(t, "appointments.more", more)}</p>
      ) : null}
    </Section>
  );
}

function detailLine(
  item: PortalAppointmentItem,
  fields: ReadonlySet<string>,
  locale: string,
  t: Translate,
): string | null {
  const parts: string[] = [];
  if (fields.has("duration") && item.durationMinutes !== null) {
    parts.push(formatMinutes(item.durationMinutes, locale, t));
  }
  if (fields.has("location") && item.locationName) parts.push(item.locationName);
  if (fields.has("staff") && item.staff.length > 0) parts.push(item.staff.map((s) => s.name).join(", "));
  if (fields.has("price") && item.price) parts.push(formatMoney(item.price, locale));
  return parts.length > 0 ? parts.join(" · ") : item.summary;
}

function ServiceRow(props: {
  item: PortalAppointmentItem;
  t: Translate;
  locale: string;
  timeZone: string;
  ctaLabel: string;
  showNextOpenings: boolean;
  fields: ReadonlySet<string>;
}): JSX.Element {
  const { item, t } = props;
  const actions = useOneStopActions();
  // `checkout` REQUIRES first name, last name and email, so this form does too.
  const visitor = useVisitor();
  const [hold, setHold] = useState<AppointmentHold | null>(null);
  const [booking, setBooking] = useState<BookingConfirmation | null>(null);
  const [alternatives, setAlternatives] = useState<readonly AvailabilitySlot[] | null>(null);

  // The slot the row books: the fragment's own soonest OPENING (not the
  // `nextOpeningAt` projection, which carries no duration and no staff), with
  // its civil time + zone resolved at the moment of use.
  //
  // SIMULATION STANDS IN FOR THE FRAGMENT, NOT FOR THE FLOW. Availability is a
  // live query, so a builder preview's appointments fragment carries no
  // openings at all — which left this row printing "No openings in this window"
  // with nothing to tap, on a page whose services are published and bookable.
  // Under `simulated` actions the row borrows one invented opening and then
  // books it through the ordinary path: hold, details, checkout, confirmation.
  // A live fragment always wins; nothing is invented when the server answered.
  const opening = item.nextOpenings[0] ?? null;
  const start = opening?.start ?? item.nextOpeningAt ?? null;

  const holdOpening = useCallback(
    () =>
      opening
        ? actions.holdOpening({ serviceId: item.serviceId, opening })
        : Promise.reject(new Error("")),
    [actions, item.serviceId, opening],
  );
  const holdCta = useCta(holdOpening, setHold);

  const checkout = useCallback(
    () =>
      hold
        ? actions.checkoutHold({ holdId: hold.holdId, visitor: visitor.value })
        : Promise.reject(new Error("")),
    [actions, hold, visitor.value],
  );
  const checkoutCta = useCta(checkout, setBooking, visitor.showServerErrors);

  // The 409 answer: what is actually free that day. Also the "pick a different
  // time" path, because they are the same question.
  const findOthers = useCallback(
    () =>
      start
        ? actions.openingsOn({ serviceId: item.serviceId, date: start.date })
        : Promise.resolve([] as readonly AvailabilitySlot[]),
    [actions, item.serviceId, start],
  );
  const othersCta = useCta(findOthers, setAlternatives);

  const holdSlot = useCallback(
    (slot: AvailabilitySlot) => actions.holdSlot({ serviceId: item.serviceId, slot }),
    [actions, item.serviceId],
  );
  const slotCta = useCta(holdSlot, (held) => {
    setHold(held);
    setAlternatives(null);
  });

  const abandon = useCallback(() => {
    // Release, then close. A hold that is neither converted nor released holds
    // the slot off the board for everyone until it lapses.
    if (hold) void actions.releaseHold({ holdId: hold.holdId }).catch(() => undefined);
    setHold(null);
    holdCta.reset();
  }, [actions, hold, holdCta]);

  const startOver = useCallback(() => {
    // NO release call here, and that is the difference from `abandon`. A hold the
    // clock has run out on is already gone, and a hold the server answered
    // `HOLD_INVALID` may have BECOME a booking — asking to release that is not a
    // request this row should be making. Drop it locally and let the visitor
    // choose again.
    setHold(null);
    holdCta.reset();
    checkoutCta.reset();
  }, [holdCta, checkoutCta]);

  // Unconditional, above the early returns: hooks do not live inside branches.
  const countdown = useCountdown(hold?.expiresAt ?? null);

  const detail = detailLine(item, props.fields, props.locale, t);
  // Weekday + time, as the prototype has it ("Mon 9:00 AM"). A full date here
  // squeezes the title column and wraps it to two or three lines; the row's own
  // geometry is what the section rhythm is measured against.
  const when = props.showNextOpenings && start ? formatWeekdayTime(start, props.locale) : null;
  const reason = actions.available ? null : actions.unavailableReason || t("cta.pending.book");
  const bookable = opening !== null;

  /* HANDOFF: the row is a door into the appointment journey, carrying the
     service the visitor tapped.

     This also settles what "No openings in this window" was doing on a page
     whose services are published and bookable. That sentence was honest about
     the FRAGMENT (availability is a live query the served document does not
     run, so `nextOpenings` is routinely empty) and wrong about the SERVICE. In
     handoff mode the row no longer needs an opening to be useful: the journey
     it opens queries real availability, so the row stays pressable and the
     soonest time is shown when the fragment happened to carry one. */
  if (actions.handoff) {
    const hand = actions.handoff;
    return (
      <button
        type="button"
        className="lv-row"
        onClick={() =>
          hand({
            kind: "appointment",
            // Server-resolved booking journey for THIS service. The journey slug
            // is tenant data, so the old hardcoded `appointment` slug resolved
            // nothing on a tenant whose journey is named anything else.
            url: item.bookUrl,
            serviceId: item.serviceId,
            locationId: item.locationId ?? null,
            label: item.name,
          })
        }
      >
        <span className="lv-row-main">
          <span className="lv-row-title">{item.name}</span>
          {detail ? <span className="lv-row-detail">{detail}</span> : null}
        </span>
        <span className="lv-row-side">
          {when ? (
            <span
              style={{
                fontSize: "var(--jrni-font-size-P2)",
                fontWeight: "var(--jrni-font-weight-bold)",
                color: "var(--jrni-color-primary-text)",
              }}
            >
              {when}
            </span>
          ) : null}
          <span
            style={{
              fontSize: "var(--jrni-font-size-L1)",
              fontWeight: "var(--jrni-font-weight-semibold)",
              color: "var(--jrni-color-primary-text)",
            }}
          >
            {props.ctaLabel}
          </span>
        </span>
      </button>
    );
  }

  if (booking) {
    return (
      <div className="lv-row" style={{ display: "block" }}>
        <SuccessPanel
          eyebrow={t("book.confirmed")}
          headline={item.name}
          /* The reference is the affordance: it becomes the link to this
             booking's own manage page when the checkout carried one, and stays
             the text it always was when it did not. The separator is the same
             glyph the joined string used — what changed is that one element can
             now be an anchor, which a joined string cannot express. */
          meta={
            <>
              {when ? `${when} · ` : null}
              <ManagedReference
                t={t}
                reference={booking.reference}
                manageUrl={booking.manageUrl}
              />
            </>
          }
        />
      </div>
    );
  }

  if (hold) {
    const running = checkoutCta.state.status === "running";
    const until = hold.expiresAt ? formatInstantTime(hold.expiresAt, props.locale) : null;
    // The clock reached zero on screen.
    const lapsed = countdown !== null && countdown <= 0;
    // The server answered about THIS hold and there is nothing safe left to
    // resubmit: `HOLD_INVALID` (it is gone, and the checkout may have gone
    // through) or `HOLD_EXPIRED` (the five minutes are up). Both keep their own
    // sentence from `CtaFeedback`; what changes is that the control the sentence
    // is talking the visitor out of stops being pressable.
    const refused =
      checkoutCta.state.status === "error" &&
      (checkoutCta.state.code === "HOLD_INVALID" || checkoutCta.state.code === "HOLD_EXPIRED");
    const spent = lapsed || refused;
    // The third part of the hold line: the ticking remainder while the hold is
    // live, the lapsed sentence at zero, and nothing at all once the server has
    // refused — a countdown still running under "we may already have your
    // booking" would be counting down something that no longer exists.
    const remainder = lapsed
      ? t("book.expired")
      : refused
        ? null
        : countdown !== null
          ? t("book.holdLeft", { t: clockOf(countdown) })
          : null;
    // The pre-flight: mark the offending fields, focus the first, send nothing.
    const submit = (): void => {
      if (visitor.check()) checkoutCta.run();
    };
    return (
      <div className="lv-row" style={{ display: "block" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <span className="lv-row-main">
            <span className="lv-panel-eyebrow">{t("book.hold.eyebrow")}</span>
            <span className="lv-row-title">{item.name}</span>
            <span className="lv-row-detail">
              {[
                formatInstantTime(hold.startUtc, props.locale),
                until ? t("book.holdUntil", { t: until }) : null,
                remainder,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </span>
          <VisitorFields form={visitor} t={t} disabled={running} />
          <Actions>
            <span className="lv-act">
              <Button
                hierarchy="primary"
                size="small"
                onClick={submit}
                /* `checkout` is NOT double-tap-safe (no Idempotency-Key yet,
                   JAI-1160): two taps can produce two bookings, and this
                   disable is the only protection it has today. It disables on
                   IN-FLIGHT — an unfinished form no longer greys the button
                   out, because a greyed button never says which field it is
                   waiting for — and on SPENT, where the hold is gone and the
                   copy beneath is asking the visitor not to submit again. */
                disabled={running || spent}
              >
                {running ? t("cta.working") : t("book.confirm")}
              </Button>
            </span>
            <span className="lv-act">
              {/* One control, two jobs, chosen by whether the hold is still
                  alive: while it is, this releases it (Cancel); once it is
                  spent there is nothing to release and the only move left is to
                  choose again. */}
              <Button
                variant="ghost"
                size="small"
                onClick={spent ? startOver : abandon}
                disabled={running}
              >
                {spent ? t("book.pickAnother") : t("visitor.cancel")}
              </Button>
            </span>
          </Actions>
          <CtaFeedback t={t} disabledReason={null} state={checkoutCta.state} />
        </div>
      </div>
    );
  }

  // The slot went while the visitor was deciding, or the hold clock ran out.
  // Both are answers: say so, and show what is free.
  const gone =
    holdCta.state.status === "error" &&
    (holdCta.state.code === "SLOT_UNAVAILABLE" || holdCta.state.code === "HOLD_EXPIRED");

  if (gone) {
    return (
      <div className="lv-row" style={{ display: "block" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <span className="lv-row-main">
            <span className="lv-row-title">{item.name}</span>
            <CtaFeedback t={t} disabledReason={null} state={holdCta.state} />
          </span>
          {alternatives === null ? (
            <Actions>
              <span className="lv-act">
                <Button
                  hierarchy="secondary"
                  size="small"
                  onClick={othersCta.run}
                  disabled={othersCta.state.status === "running"}
                >
                  {othersCta.state.status === "running" ? t("cta.working") : t("book.otherTimes")}
                </Button>
              </span>
            </Actions>
          ) : alternatives.length === 0 ? (
            <span className="lv-row-detail">{t("book.noOtherTimes")}</span>
          ) : (
            <div className="lv-slotlist">
              {alternatives.map((slot) => (
                <span className="lv-act" key={slot.startUtc}>
                  <Button
                    hierarchy="secondary"
                    size="small"
                    onClick={() => slotCta.run(slot)}
                    disabled={slotCta.state.status === "running"}
                  >
                    {formatInstantTime(slot.startUtc, props.locale)}
                  </Button>
                </span>
              ))}
            </div>
          )}
          <CtaFeedback t={t} disabledReason={null} state={othersCta.state} />
          <CtaFeedback t={t} disabledReason={null} state={slotCta.state} />
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="lv-row"
      onClick={holdCta.run}
      disabled={!bookable || reason !== null || holdCta.state.status === "running"}
      title={reason ?? undefined}
    >
      <span className="lv-row-main">
        <span className="lv-row-title">{item.name}</span>
        {detail ? <span className="lv-row-detail">{detail}</span> : null}
        {/* The reason / error line lives INSIDE the row, so a row is always one
            element and the mobile list rule keeps its dividers. */}
        <CtaFeedback t={t} disabledReason={reason} state={holdCta.state} />
      </span>
      <span className="lv-row-side">
        <span
          style={{
            fontSize: "var(--jrni-font-size-P2)",
            fontWeight: "var(--jrni-font-weight-bold)",
            color: "var(--jrni-color-primary-text)",
          }}
        >
          {when ?? t("appointments.none")}
        </span>
        <span style={{ fontSize: "var(--jrni-font-size-L1)", color: "var(--jrni-color-text-soft)" }}>
          {holdCta.state.status === "running" ? t("cta.working") : props.ctaLabel}
        </span>
      </span>
    </button>
  );
}
